import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import type { RangpurArea } from "@/content/rangpur-areas";

/**
 * Hyperlocal enrichment unique to each area. Each block renders only when the
 * area record has authored copy for it.
 */
export function AreaInsights({ a }: { a: RangpurArea }) {
  return (
    <>
      {a.searchBehavior && (
        <Section
          eyebrow={`How buyers search near ${a.name}`}
          title={
            <>
              What people actually type around <span className="text-gradient">{a.name}.</span>
            </>
          }
        >
          <Reveal>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
              {a.searchBehavior}
            </p>
            {a.localKeywords && a.localKeywords.length > 0 && (
              <div className="mt-7">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                  Queries we target near {a.name}
                </div>
                <ul className="flex flex-wrap gap-2">
                  {a.localKeywords.map((kw) => (
                    <li
                      key={kw}
                      className="rounded-full border border-border bg-card px-3.5 py-1.5 text-xs md:text-sm text-muted-foreground"
                    >
                      {kw}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Reveal>
        </Section>
      )}

      {a.localExample && (
        <Section
          eyebrow={`Local example · ${a.name}`}
          title={
            <>
              What worked for a <span className="text-gradient">{a.localExample.business.toLowerCase()}</span> here.
            </>
          }
        >
          <Reveal>
            <div className="rounded-3xl border border-border bg-card p-8 md:p-10 max-w-3xl">
              <div className="text-xs uppercase tracking-[0.2em] text-primary/80">
                {a.localExample.business} · near {a.name}
              </div>
              <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                {a.localExample.story}
              </p>
              <p className="mt-4 text-xs text-muted-foreground/70">
                Illustrative example showing the kind of work involved; it is not a verified client
                result for the {a.name} area.
              </p>
            </div>
          </Reveal>
        </Section>
      )}
    </>
  );
}
